import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  DashboardOutlined,
  ShoppingCartOutlined,
  DatabaseOutlined,
  WalletOutlined,
  RiseOutlined,
  SwapOutlined,
  UserOutlined,
  TeamOutlined,
  BankOutlined,
  TableOutlined,
  LineChartOutlined,
  FileProtectOutlined,
  FileSyncOutlined,
  WarningOutlined,
  FallOutlined,
  OrderedListOutlined,
  CalculatorOutlined,
  RightOutlined,
} from '@ant-design/icons';
import type { PanelScreen, ReportConfig } from '../types/panelConfig';
import { TierBadge } from './TierBadge';

const iconMap: Record<string, React.ReactNode> = {
  ShoppingCartOutlined: <ShoppingCartOutlined />,
  DatabaseOutlined: <DatabaseOutlined />,
  WalletOutlined: <WalletOutlined />,
  RiseOutlined: <RiseOutlined />,
  SwapOutlined: <SwapOutlined />,
  UserOutlined: <UserOutlined />,
  TeamOutlined: <TeamOutlined />,
  BankOutlined: <BankOutlined />,
  TableOutlined: <TableOutlined />,
  DashboardOutlined: <DashboardOutlined />,
  LineChartOutlined: <LineChartOutlined />,
  FileProtectOutlined: <FileProtectOutlined />,
  FileSyncOutlined: <FileSyncOutlined />,
  WarningOutlined: <WarningOutlined />,
  FallOutlined: <FallOutlined />,
  OrderedListOutlined: <OrderedListOutlined />,
  CalculatorOutlined: <CalculatorOutlined />,
};

interface DynamicSidebarProps {
  reports: ReportConfig[];
  screens?: PanelScreen[];
  collapsed?: boolean;
  /** S296: grup basliklari acilip kapanir (Layout, menu grubu olan firmada verir). Verilmezse DOM birebir eski. */
  katlanir?: boolean;
}

/** Acik gruplar bu tarayicida hatirlanir (yalniz gorunum tercihi; okunamazsa hepsi kapali baslar). */
const ACIK_GRUPLAR_ANAHTARI = 'rg-menu-acik-gruplar';

function acikGruplariOku(): Set<string> {
  try {
    const d: unknown = JSON.parse(localStorage.getItem(ACIK_GRUPLAR_ANAHTARI) ?? '[]');
    return new Set(Array.isArray(d) ? d.filter((x): x is string => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}

function acikGruplariYaz(g: Set<string>) {
  try {
    localStorage.setItem(ACIK_GRUPLAR_ANAHTARI, JSON.stringify([...g]));
  } catch {
    // Gizli pencere/engelli depolama: tercih hatirlanmaz, menu calisir.
  }
}

export function DynamicSidebar({ reports, screens = [], collapsed = false, katlanir = false }: DynamicSidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [acikGruplar, setAcikGruplar] = useState<Set<string>>(() => (katlanir ? acikGruplariOku() : new Set()));

  // G2b/K9: baslik `menuGrubu ?? category`. Map ekleme sirasi = API sirasi; panel yeniden SIRALAMAZ
  // (Gizova'da API `menu_sirasi` ile, diger firmalarda ada gore doner — ikisinde de cikti API'ninki).
  const categories = useMemo(() => {
    const m = new Map<string, ReportConfig[]>();
    for (const r of reports) {
      const grup = r.menuGrubu ?? r.category;
      const list = m.get(grup) || [];
      list.push(r);
      m.set(grup, list);
    }
    return Array.from(m.entries());
  }, [reports]);

  /* S296: acik olan sayfanin grubu acik gelir. Kullanici o grubu sonra kapatabilir; yalniz sayfa degisince
     yeniden acilir. Kalici kayda da yazilir: bir sonraki acilista ayni gorunum. */
  const aktifGrup = useMemo(
    () => categories.find(([, list]) => list.some((r) => location.pathname === `/rapor/${r.code}`))?.[0] ?? null,
    [categories, location.pathname],
  );
  useEffect(() => {
    if (!katlanir || aktifGrup === null) return;
    setAcikGruplar((g) => {
      if (g.has(aktifGrup)) return g;
      const y = new Set(g).add(aktifGrup);
      acikGruplariYaz(y);
      return y;
    });
  }, [katlanir, aktifGrup]);

  const grubuCevir = (grup: string) =>
    setAcikGruplar((g) => {
      const y = new Set(g);
      if (!y.delete(grup)) y.add(grup);
      acikGruplariYaz(y);
      return y;
    });

  return (
    <>
      <style>{sidebarCss}</style>

      <nav style={{ padding: '10px 0 20px', display: 'flex', flexDirection: 'column' }}>
        <Row
          active={location.pathname === '/'}
          onClick={() => navigate('/')}
          icon={<DashboardOutlined />}
          label="Özet"
          collapsed={collapsed}
        />

        {screens.map((s) => {
          const path = `/${s.key}`;
          return (
            <Row
              key={s.key}
              active={location.pathname === path}
              onClick={() => navigate(path)}
              icon={<LineChartOutlined />}
              label={s.title}
              collapsed={collapsed}
            />
          );
        })}

        {categories.map(([category, list]) => {
          // Dar seritte (yalniz ikon) katlama yok: baslik cizilmez, butun ikonlar gorunur (bugunku gibi).
          const katli = katlanir && !collapsed;
          const acik = !katli || acikGruplar.has(category);
          return (
            <div key={category} style={{ marginTop: 10 }}>
              {katli ? (
                <button
                  type="button"
                  className="rg-sidebar-cat rg-sidebar-cat--dugme"
                  aria-expanded={acik}
                  onClick={() => grubuCevir(category)}
                >
                  <span>{category}</span>
                  <RightOutlined aria-hidden className={`rg-sidebar-ok${acik ? ' acik' : ''}`} />
                </button>
              ) : (
                !collapsed && <div className="rg-sidebar-cat">{category}</div>
              )}
              {acik && list.map((r) => {
                const path = `/rapor/${r.code}`;
                return (
                  <Row
                    key={r.code}
                    active={location.pathname === path}
                    onClick={() => navigate(path)}
                    icon={iconMap[r.icon ?? ''] ?? <TableOutlined />}
                    label={r.name}
                    tier={r.tier}
                    collapsed={collapsed}
                  />
                );
              })}
            </div>
          );
        })}
      </nav>
    </>
  );
}

interface RowProps {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
  tier?: ReportConfig['tier'];
  collapsed: boolean;
}

function Row({ icon, label, active, onClick, tier, collapsed }: RowProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rg-sidebar-row ${active ? 'active' : ''}`}
      style={{ justifyContent: collapsed ? 'center' : 'flex-start' }}
      title={collapsed ? label : undefined}
    >
      <span className="rg-sidebar-icon">{icon}</span>
      {!collapsed && (
        <>
          <span className="rg-sidebar-label">{label}</span>
          {tier && (
            <span style={{ marginLeft: 'auto', display: 'inline-flex' }}>
              <TierBadge tier={tier} />
            </span>
          )}
        </>
      )}
    </button>
  );
}

const sidebarCss = `
  .rg-sidebar-cat {
    padding: 10px 18px 4px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.9px;
    color: var(--color-text-muted);
    text-transform: uppercase;
  }
  .rg-sidebar-cat--dugme {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    border: none;
    background: transparent;
    cursor: pointer;
    text-align: left;
  }
  .rg-sidebar-cat--dugme:hover { color: var(--color-text-primary); }
  .rg-sidebar-ok { font-size: 9px; transition: transform .15s ease; }
  .rg-sidebar-ok.acik { transform: rotate(90deg); }
  .rg-sidebar-row {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 7px 18px 7px 15px;
    border: none;
    border-left: 3px solid transparent;
    background: transparent;
    color: var(--color-text-secondary);
    font-size: 13px;
    font-weight: 400;
    cursor: pointer;
    text-align: left;
    transition: background .1s ease, color .1s ease, border-color .1s ease;
    min-height: 32px;
  }
  .rg-sidebar-row:hover {
    background: var(--color-hover-bg);
    color: var(--color-text-primary);
  }
  .rg-sidebar-row.active {
    background: var(--color-hover-bg);
    color: var(--color-text-primary);
    font-weight: 600;
    border-left-color: var(--color-primary);
  }
  .rg-sidebar-icon {
    display: inline-flex;
    align-items: center;
    font-size: 14px;
    color: var(--color-text-muted);
    flex-shrink: 0;
  }
  .rg-sidebar-row.active .rg-sidebar-icon { color: var(--color-text-primary); }
  .rg-sidebar-label {
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;
